import { BatteryCharging, ExternalLink } from 'lucide-react';
import { Button } from '@/shared/ui/button';
import BatteryControlPanel from '@/shared/components/BatteryControlPanel';
import BatteryReadout from '@/features/assets/components/BatteryReadout';
import CentralePvPanel from '@/features/assets/components/CentralePvPanel';
import PacReadout from '@/features/assets/components/PacReadout';
import {
  ageJoignabilite, etatJoignabilite, horodatageTexte, mesureTexte,
} from '@/shared/utils/liveness';

/**
 * Colonne de détail du Monitoring : identité de l'actif, relevé, pilotage, activations.
 *
 * Extrait de `MonitoringPage` qui dépassait déjà la limite de 500 lignes : la fiche est
 * l'endroit où l'on ajoute des explications (état de la machine, chiffres datés), c'est
 * donc elle qui devait sortir.
 */
export default function AssetDetailPanel({
  asset, isAdmin, batteryActions, busy, lastCommand, onBatteryCommand,
  history = [], onOpenClient, peutApparierPv = false, onApparie,
}) {
  const estBatterie = asset.type === 'Batterie';
  const estCentralePv = asset.type === 'PV';
  const idLabel = asset.type === 'PAC' ? 'Device ID' : asset.type === 'PV' ? 'ID Hoymiles' : 'ID';

  return (
    <div className="space-y-3">
      <div className="p-4 rounded-lg border border-neutral-200/80 bg-white">
        <h3 className="text-[13px] font-medium text-neutral-900 mb-3">Detail</h3>
        <div className="space-y-1.5 text-[12px]">
          <p><span className="text-neutral-400">Client:</span> <span className="font-medium">{asset.client_nom}</span></p>
          <p><span className="text-neutral-400">Ville:</span> {asset.client_ville}</p>
          <p><span className="text-neutral-400">Region:</span> {asset.client_region}</p>
          {asset.client_aggregateur && <p><span className="text-neutral-400">Agrégateur:</span> {asset.client_aggregateur}</p>}
          <p><span className="text-neutral-400">Installateur:</span> {asset.installateur_nom || '-'}</p>
          <p><span className="text-neutral-400">Équipement :</span> {asset.marque} {asset.modele}</p>
          {/* Sur une batterie, `puissance_kw` est la puissance mesurée : elle peut donc
              manquer, et « 0 kW » se lirait comme une batterie au repos. */}
          <p><span className="text-neutral-400">Puissance:</span> {mesureTexte(asset.puissance_kw, 'kW')}</p>
          <p data-testid="detail-equipment-id">
            <span className="text-neutral-400">{idLabel} :</span>{' '}
            <span className="font-mono text-[11px]">{asset.thermostat_serial || asset.identifiant_equipement}</span>
          </p>
        </div>
        {asset.client_id && (
          <Button
            size="sm"
            variant="outline"
            className="w-full mt-3 h-8 text-[11px]"
            onClick={onOpenClient}
            data-testid="open-client-from-monitoring"
          >
            <ExternalLink className="w-3 h-3 mr-1" /> Ouvrir la fiche client
          </Button>
        )}
        {/* ⚠️ LA MENTION « PILOTAGE AGRÉGATEUR VIA … » EST RETIRÉE, ET ELLE NE REVIENT PAS.
            Retour de David en vidéo, le 24/09 : « supprimer pilotage agrégateur via ».
            Elle était fausse deux fois : une PAC Tuya n'est pilotée par aucun agrégateur
            (c'est Flexcore qui écrit ses datapoints), et une centrale PV passe par
            l'API S-Miles, pas par un agrégateur. La ligne « Pilotage direct par
            Flexcore » de la batterie part avec elle : c'était la même ligne.
            `test_retours_loom_david.py` garde son absence. */}
      </div>

      {/* Quand on l'a vue pour la dernière fois, puis ses chiffres — dans cet ordre. */}
      {estBatterie && <BatteryReadout asset={asset} />}

      {/* La PAC (01/10) : ses douze grandeurs relevées chez Tuya et la courbe de 24 h.
          Un clic sur une PAC n'ouvrait que l'identité, alors que tout était relevé. */}
      {asset.type === 'PAC' && <PacReadout asset={asset} />}

      {/* Le canal PV (07/09) : son relevé, son appariement, ses onduleurs. Le panneau
          s'affiche même NON apparié — c'est là qu'il est le plus utile, puisqu'il dit
          pourquoi l'actif est muet et propose le geste qui le fait parler. */}
      {estCentralePv && (
        <CentralePvPanel asset={asset} peutApparier={peutApparierPv} onApparie={onApparie} />
      )}

      {estBatterie && isAdmin && (
        <div className="p-4 rounded-lg border border-emerald-200/80 bg-emerald-50/30" data-testid="battery-pilotage-panel">
          <h3 className="text-[13px] font-medium text-neutral-900 mb-1 flex items-center gap-1.5">
            <BatteryCharging className="w-3.5 h-3.5 text-emerald-500" /> Pilotage batterie
          </h3>
          <p className="text-[10px] text-neutral-400 mb-3">
            Passe la batterie en mode piloté et impose une consigne de puissance.
          </p>
          <div className="space-y-2.5">
            <BatteryControlPanel
              variant="panel"
              actions={batteryActions}
              etatConnexion={etatJoignabilite(asset)}
              ageS={ageJoignabilite(asset)}
              mqttStatus={asset.mqtt_status || asset.statut_connexion}
              consigne={asset.consigne_w != null ? asset : null}
              busy={busy}
              onAction={onBatteryCommand}
            />
            {/* La « puissance réelle » vivait ici, sans date : sur une batterie
                déconnectée elle affichait un chiffre périmé comme s'il était actuel.
                Elle est désormais dans le relevé au dessus, grisée et datée. */}
            {(lastCommand?.watts != null || busy) && (
              <div className="pt-2 mt-1 border-t border-emerald-100 space-y-1 text-[11px]" data-testid="battery-live-readout">
                {lastCommand?.watts != null && (
                  <div className="flex justify-between">
                    <span className="text-neutral-400">Consigne envoyée</span>
                    <span className="font-medium text-emerald-700">
                      {lastCommand.action} {lastCommand.watts} W
                    </span>
                  </div>
                )}
                {busy && <p className="text-[10px] text-neutral-400">Vérification en cours…</p>}
              </div>
            )}
          </div>
        </div>
      )}

      <div className="p-4 rounded-lg border border-neutral-200/80 bg-white">
        <h3 className="text-[13px] font-medium text-neutral-900 mb-3">Activations agrégateur</h3>
        {history.length === 0 ? (
          <p className="text-[11px] text-neutral-400 text-center py-4">Aucune activation</p>
        ) : (
          <div className="space-y-1.5 max-h-[350px] overflow-y-auto">
            {history.map(h => (
              <div key={h.id} className="flex items-start gap-2 py-1.5 border-b border-neutral-50 last:border-0">
                <div className={`w-1.5 h-1.5 rounded-full mt-1.5 flex-shrink-0 ${h.type === 'effacement' ? 'bg-blue-400' : 'bg-amber-400'}`} />
                <div className="flex-1">
                  <div className="flex items-center gap-1.5">
                    <span className="text-[10px] text-neutral-500 font-medium">{h.type === 'effacement' ? 'Effacement' : 'Modulation'}</span>
                    <span className="text-[10px] text-neutral-400">{h.duree_minutes} min</span>
                  </div>
                  <p className="text-[10px] text-neutral-400">{horodatageTexte(h.debut) || '-'}</p>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
